/**
 * Fees module — P6B+C+D overhaul.
 *   • Comprehensive filter sidebar (status, fee type, payment mode,
 *     student/family search, date ranges, instalment, due windows)
 *   • Live dashboard cards + 4 charts (monthly trend, fee-head mix,
 *     payment-mode mix, ageing buckets) — fetched in one shot from
 *     /finance/dashboard.
 *   • Enhanced "Record payment" modal: cash/UPI/card/cheque/bank-transfer
 *     /store-UPI modes, reference, payment date, remarks, proof note.
 *   • Per-row PDF receipt download (calls /payments/{id}/receipt.pdf).
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatMoney } from "@/lib/api";
import { PageHeader, Panel, Pill } from "@/components/Bits";
import { toast } from "sonner";
import {
  MagnifyingGlass, Funnel, FileArrowDown, X, Download,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";

const STATUS_OPTIONS = ["", "paid", "partial", "due", "overdue", "cancelled"];
const FEE_TYPES = ["", "admission", "tuition", "daycare", "transport", "meal",
  "activity", "event", "uniform", "books", "kit", "late_fee", "misc"];
const PAYMENT_MODES = ["", "UPI", "Card", "Cash", "Bank Transfer", "Cheque",
  "Store UPI", "Online", "Razorpay", "Stripe", "Wallet", "QR"];
const COLORS = ["#E56B55", "#F5A623", "#6B8E5A", "#3E92CC", "#9E5AB5",
                "#D9534F", "#7B6451", "#5C7CFA"];

export default function Fees() {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [dash, setDash] = useState(null);
  const [paying, setPaying] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    q: "", status: "", fee_type: "", payment_mode: "",
    due_from: "", due_to: "", paid_from: "", paid_to: "",
    overdue_only: false, due_today: false, due_this_week: false,
    due_this_month: false, has_instalment: "",
  });

  // Build the params object once so both load() and CSV/PDF export share it.
  const params = useMemo(() => {
    const out = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false) return;
      out[k] = v;
    });
    return out;
  }, [filters]);

  const load = async () => {
    try {
      const [i, p, d] = await Promise.all([
        api.get("/invoices", { params }),
        api.get("/payments"),
        api.get("/finance/dashboard"),
      ]);
      setInvoices(i.data || []);
      setPayments(p.data || []);
      setDash(d.data);
    } catch (err) {
      toast.error("Could not load fees");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

  const downloadCSV = () => {
    const rows = [
      ["Invoice", "Period", "Total", "Paid", "Due", "Status"],
      ...invoices.map((i) => [
        i.invoice_id, i.period || "",
        i.amount_total, i.amount_paid, i.amount_due, i.status,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `fees-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const cards = dash?.cards || {};

  return (
    <div data-testid="fees-page">
      <PageHeader
        eyebrow="Fees & Payments"
        title="Money in motion, calmly."
        subtitle="Comprehensive filters, branch-wise plans and one-click receipts."
        action={
          <div className="flex gap-2">
            <button
              data-testid="fees-csv-export"
              onClick={downloadCSV}
              className="px-4 h-10 rounded-full border border-border text-sm flex items-center gap-2 bg-white hover:bg-paper"
            >
              <FileArrowDown size={16} weight="bold" /> Export CSV
            </button>
            <button
              data-testid="fees-filter-toggle"
              onClick={() => setFiltersOpen((v) => !v)}
              className="px-4 h-10 rounded-full bg-ink text-paper text-sm flex items-center gap-2"
            >
              <Funnel size={16} weight="bold" /> {filtersOpen ? "Hide filters" : "Filters"}
            </button>
          </div>
        }
      />

      {/* ─── Dashboard cards ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Card label="Billed" value={formatMoney(cards.billed || 0)} testid="fees-stat-billed" />
        <Card label="Collected" value={formatMoney(cards.collected || 0)} tone="sage" testid="fees-stat-collected" />
        <Card label="Outstanding" value={formatMoney(cards.outstanding || 0)} tone="terracotta" testid="fees-stat-due" />
        <Card label="Overdue" value={formatMoney(cards.overdue || 0)} tone="terracotta" testid="fees-stat-overdue" />
        <Card label="Collection %" value={(cards.collection_pct ?? 0) + "%"} tone="mustard" testid="fees-stat-pct" />
      </div>

      {/* ─── Charts row ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Panel title="Monthly collection" testid="chart-monthly">
          {dash?.monthly?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={dash.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE5DF" />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Line type="monotone" dataKey="collected" stroke="#E56B55" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty label="No payments yet" />}
        </Panel>
        <Panel title="Fee-head mix" testid="chart-fee-head">
          {dash?.fee_head_mix?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={dash.fee_head_mix} dataKey="amount" nameKey="fee_head"
                  cx="50%" cy="50%" outerRadius={75} label={(d) => d.fee_head}>
                  {dash.fee_head_mix.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty label="No invoices yet" />}
        </Panel>
        <Panel title="Payment mode mix" testid="chart-payment-mode">
          {dash?.payment_mode_mix?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dash.payment_mode_mix}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE5DF" />
                <XAxis dataKey="mode" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="amount" fill="#6B8E5A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty label="No payments yet" />}
        </Panel>
        <Panel title="Overdue ageing" testid="chart-ageing">
          {dash?.ageing?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dash.ageing}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE5DF" />
                <XAxis dataKey="bucket" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="amount" fill="#E56B55" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty label="No ageing data" />}
        </Panel>
      </div>

      {/* ─── Branch breakdown (HQ + super only) ─── */}
      {dash?.branches?.length > 0 && (
        <Panel title="Branch breakdown" testid="branch-breakdown" className="mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-ink-soft text-xs uppercase tracking-wider text-left">
                <th className="py-2">Branch</th><th>City</th><th>Billed</th><th>Collected</th><th>Outstanding</th><th>%</th>
              </tr></thead>
              <tbody>
                {dash.branches.map((b) => (
                  <tr key={b.branch_id} className="border-t border-border" data-testid={`branch-row-${b.branch_id}`}>
                    <td className="py-3 font-medium">{b.name}</td>
                    <td>{b.city}</td>
                    <td className="font-medium">{formatMoney(b.billed)}</td>
                    <td className="text-sage-deep">{formatMoney(b.paid)}</td>
                    <td className="text-terracotta">{formatMoney(b.due)}</td>
                    <td>{b.billed ? Math.round((b.paid / b.billed) * 100) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* ─── Filter panel ─── */}
      {filtersOpen && (
        <Panel title="Filters" className="mb-6" testid="filters-panel">
          <FilterGrid filters={filters} setFilters={setFilters} />
        </Panel>
      )}

      {/* ─── Invoices table ─── */}
      <Panel title={`Invoices · ${invoices.length}`} testid="invoices-panel">
        {invoices.length === 0 ? (
          <Empty label="No invoices match your filters" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-ink-soft text-xs uppercase tracking-wider text-left">
                <th className="py-2">Invoice</th><th>Period</th><th>Fee type</th><th>Total</th><th>Paid</th><th>Due</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {invoices.slice(0, 60).map((inv) => (
                  <tr key={inv.invoice_id} className="border-t border-border">
                    <td className="py-3 font-medium" data-testid={`row-${inv.invoice_id}`}>{inv.invoice_id.slice(-6).toUpperCase()}</td>
                    <td>{inv.period || "—"}</td>
                    <td className="text-xs capitalize">{inv.fee_type || inv.category || "tuition"}</td>
                    <td className="font-medium">{formatMoney(inv.amount_total, inv.currency)}</td>
                    <td className="text-sage-deep">{formatMoney(inv.amount_paid, inv.currency)}</td>
                    <td className="text-terracotta">{formatMoney(inv.amount_due, inv.currency)}</td>
                    <td><Pill tone={inv.status === "paid" ? "sage" : inv.status === "partial" ? "mustard" : "terracotta"}>{inv.status}</Pill></td>
                    <td className="space-x-2">
                      {inv.status !== "paid" && (
                        <button onClick={() => setPaying(inv)} className="text-xs px-3 py-1.5 rounded-full bg-terracotta text-white" data-testid={`pay-${inv.invoice_id}`}>Record payment</button>
                      )}
                      {inv.amount_paid > 0 && (
                        <DownloadReceiptButton invoiceId={inv.invoice_id} payments={payments} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {paying && <PayModal inv={paying} onClose={() => setPaying(null)} onDone={() => { setPaying(null); load(); }} />}
    </div>
  );
}

/* ─────────────────────── helpers ─────────────────────── */

function Card({ label, value, tone, testid }) {
  const cls = tone === "sage" ? "text-sage-deep"
    : tone === "terracotta" ? "text-terracotta"
    : tone === "mustard" ? "text-mustard-hover" : "";
  return (
    <Panel testid={testid}>
      <div className="text-sm text-ink-soft">{label}</div>
      <div className={`font-heading font-bold text-2xl mt-1 ${cls}`}>{value}</div>
    </Panel>
  );
}

function Empty({ label }) {
  return <div className="py-10 text-center text-sm text-ink-soft">{label}</div>;
}

function FilterGrid({ filters, setFilters }) {
  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <Input label="Search (student/parent/phone/email)" icon={<MagnifyingGlass />}
        value={filters.q} onChange={(v) => upd("q", v)} testid="filter-search" placeholder="e.g. Aarav, +91…" />
      <Select label="Status" value={filters.status} onChange={(v) => upd("status", v)}
        testid="filter-status" options={STATUS_OPTIONS.map((s) => ({ v: s, l: s || "Any" }))} />
      <Select label="Fee type" value={filters.fee_type} onChange={(v) => upd("fee_type", v)}
        testid="filter-fee-type" options={FEE_TYPES.map((f) => ({ v: f, l: f || "Any" }))} />
      <Select label="Payment mode" value={filters.payment_mode} onChange={(v) => upd("payment_mode", v)}
        testid="filter-mode" options={PAYMENT_MODES.map((m) => ({ v: m, l: m || "Any" }))} />
      <Input type="date" label="Due from" value={filters.due_from} onChange={(v) => upd("due_from", v)} testid="filter-due-from" />
      <Input type="date" label="Due to" value={filters.due_to} onChange={(v) => upd("due_to", v)} testid="filter-due-to" />
      <Input type="date" label="Paid from" value={filters.paid_from} onChange={(v) => upd("paid_from", v)} testid="filter-paid-from" />
      <Input type="date" label="Paid to" value={filters.paid_to} onChange={(v) => upd("paid_to", v)} testid="filter-paid-to" />
      <div className="lg:col-span-3 flex flex-wrap gap-3 pt-2 text-sm">
        <Toggle label="Only overdue" checked={filters.overdue_only} onChange={(v) => upd("overdue_only", v)} testid="filter-overdue" />
        <Toggle label="Due today" checked={filters.due_today} onChange={(v) => upd("due_today", v)} testid="filter-due-today" />
        <Toggle label="Due this week" checked={filters.due_this_week} onChange={(v) => upd("due_this_week", v)} testid="filter-due-week" />
        <Toggle label="Due this month" checked={filters.due_this_month} onChange={(v) => upd("due_this_month", v)} testid="filter-due-month" />
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid, icon }) {
  return (
    <label className="block">
      <span className="text-xs text-ink-soft block mb-1">{label}</span>
      <div className="relative">
        {icon && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-soft">{icon}</span>}
        <input
          type={type} value={value || ""} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className={`w-full h-10 ${icon ? "pl-8" : "pl-3"} pr-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta placeholder:text-ink-soft/60`}
        />
      </div>
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-ink-soft block mb-1">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-10 px-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-1.5 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-terracotta" data-testid={testid} />
      {label}
    </label>
  );
}

function DownloadReceiptButton({ invoiceId, payments }) {
  const latest = payments.find((p) => p.invoice_id === invoiceId);
  if (!latest) return null;
  const onClick = async () => {
    try {
      const r = await api.get(`/payments/${latest.payment_id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `receipt-${latest.receipt_no || latest.payment_id.slice(-6)}.pdf`; a.click();
    } catch { toast.error("Could not download receipt"); }
  };
  return (
    <button onClick={onClick} className="text-xs px-3 py-1.5 rounded-full border border-border inline-flex items-center gap-1" data-testid={`receipt-${invoiceId}`}>
      <Download size={12} weight="bold" /> Receipt
    </button>
  );
}

function PayModal({ inv, onClose, onDone }) {
  const [amount, setAmount] = useState(inv.amount_due);
  const [mode, setMode] = useState("Cash");
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/payments", {
        invoice_id: inv.invoice_id,
        amount: Number(amount),
        mode,
        reference: reference || undefined,
        paid_at: paidAt,
        remarks: remarks || undefined,
      });
      toast.success("Payment recorded · email sent to parent");
      onDone();
    } catch { toast.error("Could not record payment"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl border border-border p-6 space-y-4" data-testid="pay-modal">
        <div className="flex items-start justify-between">
          <h3 className="font-heading text-xl font-bold">Record manual payment</h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={18} weight="bold" /></button>
        </div>
        <div className="text-sm text-ink-soft">
          Outstanding: <span className="text-terracotta font-semibold">{formatMoney(inv.amount_due, inv.currency)}</span>
          <br />Invoice #{inv.invoice_id.slice(-6).toUpperCase()}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-ink-soft block mb-1">Amount received</span>
            <input type="number" min={1} max={inv.amount_due} value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full h-11 px-3 rounded-xl border border-border" required data-testid="pay-amount" />
          </label>
          <label className="block">
            <span className="text-xs text-ink-soft block mb-1">Payment mode</span>
            <select value={mode} onChange={(e) => setMode(e.target.value)}
              className="w-full h-11 px-3 rounded-xl border border-border" data-testid="pay-mode">
              {["Cash", "UPI", "Card", "Bank Transfer", "Cheque", "Store UPI", "Online", "Wallet", "QR"].map((m) => <option key={m}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-ink-soft block mb-1">Reference / UPI / Cheque #</span>
            <input value={reference} onChange={(e) => setReference(e.target.value)}
              className="w-full h-11 px-3 rounded-xl border border-border placeholder:text-ink-soft/60"
              placeholder="e.g. 1234567890 / CHQ-78" data-testid="pay-reference" />
          </label>
          <label className="block">
            <span className="text-xs text-ink-soft block mb-1">Payment date</span>
            <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
              className="w-full h-11 px-3 rounded-xl border border-border" data-testid="pay-date" />
          </label>
        </div>
        <label className="block">
          <span className="text-xs text-ink-soft block mb-1">Remarks (optional)</span>
          <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
            placeholder="e.g. Paid by grandparent in cash at office desk"
            className="w-full px-3 py-2 rounded-xl border border-border placeholder:text-ink-soft/60" data-testid="pay-remarks" />
        </label>
        <div className="rounded-xl bg-sage-light/40 p-3 text-xs text-sage-deep border border-sage-light">
          A branded PDF receipt will be generated and emailed to the parent automatically.
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-border">Cancel</button>
          <button disabled={loading} className="flex-1 h-11 rounded-full bg-terracotta text-white font-semibold" data-testid="pay-submit">
            {loading ? "Saving..." : "Record & email"}
          </button>
        </div>
      </form>
    </div>
  );
}
